import { CouponType, couponTypes } from '@app/types/cart/Coupon';
import Box from '@redbubble/design-system/src/Box';
import Card from '@redbubble/design-system/src/Card';
import Text from '@redbubble/design-system/src/Text';
import TextLink from '@redbubble/design-system/src/TextLink';
import { FormattedMessage, useIntl } from '@redbubble/next/intl';
import React, { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import styles from '../Coupon.module.scss';
import { couponMessages } from '../messages';
import { CouponAppliedIcon } from './CouponAppliedIcon';
import { useDateRangeMessage } from './useDateRangeMessage';

const percentCouponTypes = new Set<CouponType>([
  couponTypes.percentOff,
  couponTypes.variablePercentOff,
]);

interface CouponProps {
  displayName: string;
  type: CouponType;
  expiryDate: string;
  locale: string;
}

export const Coupon: FC<CouponProps> = ({
  displayName,
  type,
  expiryDate,
  locale,
}) => {
  const intl = useIntl();
  const disclaimerLink = `https://help.redbubble.com/hc/${locale}/articles/360020931011`;
  const dateRange: [Date, Date] = [new Date(), new Date(expiryDate)];
  const { ref, inView } = useInView({ triggerOnce: true });
  const expiryDateRangeMessage = useDateRangeMessage(locale, ...dateRange);

  return (
    <Box marginX="xxxs">
      <Card>
        <Box
          display="flex"
          alignItems="center"
          paddingY="s"
          paddingX="xxs"
          paddingLeft="s"
        >
          <div ref={ref}>
            <CouponAppliedIcon inView={inView} />
          </div>

          <Box marginLeft="s">
            <Text display="block" type="display6">
              <strong>{displayName}</strong>
            </Text>
            <Text type="body2">{expiryDateRangeMessage}</Text>
          </Box>
        </Box>
      </Card>
      {percentCouponTypes.has(type) && (
        <Box className={styles.additionalMessage} marginBottom="s">
          <Text muted type="caption">
            {intl.formatMessage(couponMessages.percentageOffDisclaimer)}
          </Text>
          &nbsp;
          <TextLink href={disclaimerLink} target="_blank">
            <Text type="caption" muted>
              {intl.formatMessage(couponMessages.learnMore)}
              <span className="clipped">
                <FormattedMessage
                  defaultMessage=" - opens in new window or tab"
                  id="opens-in-new-window-message"
                />
              </span>
            </Text>
          </TextLink>
        </Box>
      )}
    </Box>
  );
};
